import React, { useState, useEffect } from 'react';
import {
  HeartIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  BeakerIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface HealthMedicalInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: 'diagnostic-tree' | 'anatomy-identification' | 'drug-interaction' | 'case-study' | 'procedure-simulation';
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const HealthMedicalInteractive: React.FC<HealthMedicalInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(120); // 2 minutes
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your medical assessment.', {
          icon: '🏥',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    switch (exerciseType) {
      case 'diagnostic-tree':
        setInputs({
          symptoms: [],
          diagnosis: '',
          reasoning: '',
          differentialDiagnoses: ['', ''],
          recommendedTests: []
        });
        break;
      case 'anatomy-identification':
        setInputs({
          identifications: scenario?.anatomyItems?.map(() => '') || [],
          functions: scenario?.anatomyItems?.map(() => '') || []
        });
        break;
      case 'drug-interaction':
        setInputs({
          interactions: '',
          contraindications: '',
          dosage: '',
          monitoring: '',
          patientEducation: ''
        });
        break;
      case 'case-study':
        setInputs({
          assessment: '',
          treatmentPlan: '',
          prognosis: '',
          followUp: '',
          complications: ''
        });
        break;
      case 'procedure-simulation':
        setInputs({
          preparation: [],
          steps: [],
          complications: '',
          postCare: ''
        });
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes on the assessment.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'diagnostic-tree':
        return ['diagnosis', 'reasoning'];
      case 'anatomy-identification':
        return ['identifications'];
      case 'drug-interaction':
        return ['interactions'];
      case 'case-study':
        return ['assessment', 'treatmentPlan'];
      case 'procedure-simulation':
        return ['steps'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'diagnostic-tree':
        return evaluateDiagnostic();
      case 'anatomy-identification':
        return evaluateAnatomy();
      case 'drug-interaction':
        return evaluateDrugInteraction();
      case 'case-study':
        return evaluateCaseStudy();
      case 'procedure-simulation':
        return evaluateProcedure();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateDiagnostic = () => {
    let score = 0;
    const feedback = [];

    // Diagnosis accuracy (40 points)
    if (inputs.diagnosis?.toLowerCase().includes(scenario?.correctDiagnosis?.toLowerCase())) {
      score += 40;
      feedback.push('✓ Correct primary diagnosis');
    } else {
      feedback.push('Consider reviewing patient symptoms and clinical presentation');
    }

    // Clinical reasoning (30 points)
    if (inputs.reasoning?.length > 60) {
      score += 30;
      feedback.push('✓ Comprehensive clinical reasoning provided');
    } else {
      feedback.push('Expand your clinical reasoning and rationale');
    }

    // Differential diagnoses (20 points)
    const validDifferentials = inputs.differentialDiagnoses?.filter((d: string) => d.length > 5).length || 0;
    score += Math.min(20, validDifferentials * 10);

    // Recommended tests (10 points)
    if (inputs.recommendedTests?.length > 0) {
      score += 10;
      feedback.push('✓ Diagnostic tests recommended');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateAnatomy = () => {
    let score = 0;
    const feedback = [];

    const correctIds = inputs.identifications?.filter((id: string, index: number) => 
      id.toLowerCase().includes(scenario?.anatomyItems?.[index]?.correct?.toLowerCase())
    ).length || 0;
    
    const totalItems = scenario?.anatomyItems?.length || 1;
    score = Math.round((correctIds / totalItems) * 100);
    
    feedback.push(`Correctly identified ${correctIds}/${totalItems} anatomical structures`);
    if (score >= 70) feedback.push('✓ Good anatomical knowledge demonstrated');

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateDrugInteraction = () => {
    let score = 0;
    const feedback = [];

    // Interaction identification (40 points)
    if (inputs.interactions?.length > 50) {
      score += 40;
      feedback.push('✓ Drug interactions identified');
    }

    // Contraindications (25 points)
    if (inputs.contraindications?.length > 30) {
      score += 25;
      feedback.push('✓ Contraindications noted');
    }

    // Dosage considerations (20 points)
    if (inputs.dosage?.length > 20) {
      score += 20;
      feedback.push('✓ Dosage considerations addressed');
    }

    // Patient education (15 points)
    if (inputs.patientEducation?.length > 30) {
      score += 15;
      feedback.push('✓ Patient education included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateCaseStudy = () => {
    let score = 0;
    const feedback = [];

    // Clinical assessment (35 points)
    if (inputs.assessment?.length > 80) {
      score += 35;
      feedback.push('✓ Thorough clinical assessment');
    }

    // Treatment plan (35 points)
    if (inputs.treatmentPlan?.length > 60) {
      score += 35;
      feedback.push('✓ Comprehensive treatment plan');
    }

    // Prognosis (15 points)
    if (inputs.prognosis?.length > 30) {
      score += 15;
      feedback.push('✓ Prognosis provided');
    }

    // Follow-up (15 points)
    if (inputs.followUp?.length > 20) {
      score += 15;
      feedback.push('✓ Follow-up plan included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateProcedure = () => {
    let score = 0;
    const feedback = [];

    // Procedure steps (60 points)
    const validSteps = inputs.steps?.filter((step: string) => step.length > 15).length || 0;
    const expectedSteps = scenario?.procedureSteps?.length || 5;
    score += Math.min(60, (validSteps / expectedSteps) * 60);

    // Preparation (20 points)
    const validPrep = inputs.preparation?.filter((prep: string) => prep.length > 10).length || 0;
    if (validPrep >= 2) {
      score += 20;
      feedback.push('✓ Adequate preparation steps');
    }

    // Post-procedure care (20 points)
    if (inputs.postCare?.length > 40) {
      score += 20;
      feedback.push('✓ Post-procedure care addressed');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent clinical work! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'diagnostic-tree': return <ClipboardDocumentCheckIcon className="h-6 w-6" />;
      case 'anatomy-identification': return <UserIcon className="h-6 w-6" />;
      case 'drug-interaction': return <BeakerIcon className="h-6 w-6" />;
      case 'case-study': return <MagnifyingGlassIcon className="h-6 w-6" />;
      case 'procedure-simulation': return <HeartIcon className="h-6 w-6" />;
      default: return <HeartIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'diagnostic-tree':
        return (
          <div className="space-y-6">
            {scenario?.patientInfo && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Patient Information:</h4>
                <div className="text-sm text-gray-700">{scenario.patientInfo}</div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Diagnosis *
              </label>
              <input
                type="text"
                value={inputs.diagnosis || ''}
                onChange={(e) => handleInputChange('diagnosis', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                placeholder="Enter your primary diagnosis"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clinical Reasoning *
              </label>
              <textarea
                value={inputs.reasoning || ''}
                onChange={(e) => handleInputChange('reasoning', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                rows={4}
                placeholder="Explain your diagnostic reasoning based on symptoms and clinical findings"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Differential Diagnoses
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.differentialDiagnoses?.[index] || ''}
                  onChange={(e) => {
                    const newDiffs = [...(inputs.differentialDiagnoses || ['', ''])];
                    newDiffs[index] = e.target.value;
                    handleInputChange('differentialDiagnoses', newDiffs);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 mb-2"
                  placeholder={`Differential diagnosis ${index + 1}`}
                  disabled={isSubmitted}
                />
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="mb-4">
              {getExerciseIcon()}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {exerciseType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} Exercise
            </h4>
            <p className="text-gray-600 mb-6">
              This health & medical exercise is being developed with specialized medical assessments.
            </p>
            
            <button
              onClick={() => onComplete(true, 85)}
              className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Complete Exercise (Temporary)
            </button>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-red-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum assessment time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-red-600 hover:text-red-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Submit Assessment
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthMedicalInteractive;